/**
 * Navigation Manager Module
 * Handles page navigation, URL updates, and navigation state
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only navigation logic
 * - Encapsulation: Navigation state is private
 * - Loose Coupling: Communicates via callbacks, not direct dependencies
 */

import { PageName, PageConfig, NavigationEvent } from '../types/page-types';

/**
 * Callback type for page initialization
 */
type PageInitCallback = (pageName: PageName) => void | Promise<void>;

/**
 * NavigationManager class
 * Manages page navigation and active states
 */
export class NavigationManager {
    private currentPage: PageName;
    private pages: Map<PageName, PageConfig>;
    private onPageChange: PageInitCallback | null = null;
    private navigationSetup: boolean = false;

    constructor() {
        this.currentPage = 'dashboard';
        this.pages = new Map<PageName, PageConfig>();
    }

    /**
     * Register a page configuration
     */
    public registerPage(pageName: PageName, config: PageConfig): void {
        this.pages.set(pageName, config);
    }

    /**
     * Register multiple pages at once
     */
    public registerPages(pageConfigs: Map<PageName, PageConfig>): void {
        pageConfigs.forEach((config, name) => {
            this.pages.set(name, config);
        });
    }

    /**
     * Set callback for page change events
     */
    public setPageChangeCallback(callback: PageInitCallback): void {
        this.onPageChange = callback;
    }

    /**
     * Setup navigation click handlers
     */
    public setupNavigation(): void {
        // Prevent duplicate listener setup
        if (this.navigationSetup) {
            console.log('⚠️ Navigation already setup, skipping to prevent duplicate listeners');
            return;
        }

        document.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const pageLink = target.closest('[data-page]') as HTMLElement;
            if (pageLink) {
                e.preventDefault();
                e.stopPropagation();
                const pageName = pageLink.dataset.page as PageName;
                this.showPage(pageName);
            }
        });

        // Setup mobile menu toggle
        const mobileMenuButton = document.getElementById('mobile-menu-button');
        const mobileMenu = document.getElementById('mobile-menu');

        if (mobileMenuButton && mobileMenu) {
            mobileMenuButton.addEventListener('click', () => {
                mobileMenu.classList.toggle('hidden');
            });
        }

        this.navigationSetup = true;
        console.log('✅ Navigation click handlers setup complete');
    }

    /**
     * Navigate to a specific page
     * @param pageName - Name of the page to show
     */
    public showPage(pageName: PageName): void {
        if (!this.pages.has(pageName)) {
            console.error(`Page "${pageName}" not found`);
            return;
        }

        console.log(`Switching to page: ${pageName}`);

        const previousPage = this.currentPage;

        // Hide all page content
        this.hideAllPages();

        // Update navigation UI
        this.updateNavigationUI(pageName);

        // Show target page content
        this.showPageContent(pageName);

        // Update current page state
        this.currentPage = pageName;

        // Update browser title
        this.updatePageTitle(pageName);

        // Close mobile menu if open
        this.closeMobileMenu();

        // Trigger page change callback (for initialization)
        if (this.onPageChange) {
            this.onPageChange(pageName);
        }

        // Log navigation event
        const event: NavigationEvent = {
            fromPage: previousPage,
            toPage: pageName,
            timestamp: Date.now()
        };
        console.log('Navigation event:', event);
    }

    /**
     * Get current active page
     */
    public getCurrentPage(): PageName {
        return this.currentPage;
    }

    /**
     * Get page configuration
     */
    public getPageConfig(pageName: PageName): PageConfig | undefined {
        return this.pages.get(pageName);
    }

    /**
     * Check if a page is registered
     */
    public hasPage(pageName: PageName): boolean {
        return this.pages.has(pageName);
    }

    /**
     * Hide all page content elements
     */
    private hideAllPages(): void {
        document.querySelectorAll('[id$="-content"]').forEach(page => {
            page.classList.add('hidden');
        });
    }

    /**
     * Update navigation active states in the UI
     */
    private updateNavigationUI(pageName: PageName): void {
        // Remove active class from all nav items
        document.querySelectorAll('[data-page]').forEach(navItem => {
            // Handle sidebar items
            if (navItem.classList.contains('sidebar-item')) {
                navItem.classList.remove('active');
            }
            // Handle mobile/top nav items
            navItem.classList.remove('bg-blue-700', 'text-white');
            navItem.classList.add('text-blue-100', 'hover:text-white', 'hover:bg-blue-600');
        });

        // Add active class to current page nav item
        const pageConfig = this.pages.get(pageName);
        if (pageConfig && pageConfig.navSelector) {
            const navItems = document.querySelectorAll(pageConfig.navSelector);
            navItems.forEach(navItem => {
                // Handle sidebar items
                if (navItem.classList.contains('sidebar-item')) {
                    navItem.classList.add('active');
                }
                // Handle mobile/top nav items
                navItem.classList.remove('text-blue-100', 'hover:text-white', 'hover:bg-blue-600');
                navItem.classList.add('bg-blue-700', 'text-white');
            });
        }
    }

    /**
     * Show page content element
     */
    private showPageContent(pageName: PageName): void {
        const pageConfig = this.pages.get(pageName);
        if (pageConfig && pageConfig.contentSelector) {
            const pageElement = document.querySelector(pageConfig.contentSelector);
            if (pageElement) {
                pageElement.classList.remove('hidden');
            } else {
                console.error(`Page content element not found: ${pageConfig.contentSelector}`);
            }
        }
    }

    /**
     * Update browser page title
     */
    private updatePageTitle(pageName: PageName): void {
        const pageConfig = this.pages.get(pageName);
        if (pageConfig) {
            document.title = `ASD ICP - ${pageConfig.title}`;
        }
    }

    /**
     * Close mobile menu if open
     */
    private closeMobileMenu(): void {
        const mobileMenu = document.getElementById('mobile-menu');
        if (mobileMenu && !mobileMenu.classList.contains('hidden')) {
            mobileMenu.classList.add('hidden');
        }
    }
}
